import { ThemeEvents } from '@theme/events';

const NEGATIVE_OPTION_VALUES = new Set([
  'no thank you',
  'no thanks',
  'no',
  'none',
  'not included',
]);

class GVSStickyAtc extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.media = window.matchMedia('(max-width: 749px)');
    this.button = this.querySelector('[data-gvs-sticky-atc-button]');
    this.buttonText = this.querySelector('[data-gvs-sticky-atc-text]');
    this.variantLabel = this.querySelector('[data-gvs-sticky-atc-variant]');
    this.priceLabel = this.querySelector('[data-gvs-sticky-atc-price]');
    this.productId = this.dataset.productId || '';
    this.hasVariants = this.dataset.hasVariants === 'true';
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'GBP';
    this.locale = document.documentElement.lang || 'en-GB';
    this.addedTimer = null;
    this.pendingTimer = null;
    this.visibilityRetryTimer = null;
    this.pendingAdd = false;
    this.controller = new AbortController();

    if (!this.button || !this.buttonText) return;

    this.handleClick = this.handleClick.bind(this);
    this.handleVariantSelected = this.handleVariantSelected.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleOptionChange = this.handleOptionChange.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleCartError = this.handleCartError.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleVisualViewport = this.handleVisualViewport.bind(this);

    const { signal } = this.controller;

    this.button.addEventListener('click', this.handleClick, { signal });
    const scope = this.getScope();
    scope.addEventListener(ThemeEvents.variantSelected, this.handleVariantSelected, { signal });
    scope.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal });
    window.addEventListener(ThemeEvents.cartError, this.handleCartError, { signal });
    this.media.addEventListener('change', this.handleViewportChange, { signal });
    window.addEventListener('pageshow', this.handleViewportChange, { signal });

    scope.addEventListener('change', this.handleOptionChange, { signal });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleVisualViewport, { signal });
    }

    this.resizeObserver = new ResizeObserver(() => this.syncPageSpace());
    this.resizeObserver.observe(this);

    this.updateButtonState();
    this.handleViewportChange();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();

    if (this.addedTimer) window.clearTimeout(this.addedTimer);
    if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
    if (this.visibilityRetryTimer) window.clearTimeout(this.visibilityRetryTimer);

    this.hide();
    document.body.classList.remove('gvs-mobile-keyboard-open');
  }

  getScope() {
    return this.closest('.shopify-section') || document;
  }

  getRealButton() {
    const scope = this.getScope();

    return (
      scope.querySelector('.buy-buttons-block button[name="add"]') ||
      scope.querySelector('product-form-component button[name="add"]') ||
      scope.querySelector('button.add-to-cart-button')
    );
  }

  getPurchaseTarget() {
    const scope = this.getScope();

    return (
      scope.querySelector('.buy-buttons-block') ||
      scope.querySelector('product-form-component') ||
      scope.querySelector('[data-testid="product-information-details"]')
    );
  }

  getVariantPicker() {
    const scope = this.getScope();

    return (
      scope.querySelector('variant-picker') ||
      scope.querySelector('variant-picker-component') ||
      scope.querySelector('.variant-picker') ||
      scope.querySelector('fieldset.variant-option')
    );
  }

  initVisibilityObserver() {
    this.visibilityObserver?.disconnect();
    if (this.visibilityRetryTimer) window.clearTimeout(this.visibilityRetryTimer);
    this.visibilityRetryTimer = null;

    if (!this.media.matches) {
      this.hide();
      return;
    }

    const target = this.getPurchaseTarget();

    if (!target) {
      this.visibilityRetryTimer = window.setTimeout(() => {
        this.visibilityRetryTimer = null;
        if (this.isConnected) this.initVisibilityObserver();
      }, 350);
      return;
    }

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting) {
          this.hide();
        } else {
          this.show();
        }
      },
      {
        threshold: 0.18,
        rootMargin: `-${Math.max(0, this.getHeaderHeight())}px 0px 0px 0px`,
      }
    );

    this.visibilityObserver.observe(target);
  }

  getHeaderHeight() {
    const value = Number.parseFloat(
      getComputedStyle(document.body).getPropertyValue('--header-height')
    );

    return Number.isFinite(value) ? value : 0;
  }

  show() {
    if (!this.media.matches) return;

    this.classList.add('is-visible');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('gvs-sticky-atc-visible');
    this.syncPageSpace();
  }

  hide() {
    this.classList.remove('is-visible');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('gvs-sticky-atc-visible');
    document.documentElement.style.setProperty('--gvs-mobile-bottom-action-height', '0px');
  }

  syncPageSpace() {
    if (!this.classList.contains('is-visible') || !this.media.matches) return;

    document.documentElement.style.setProperty(
      '--gvs-mobile-bottom-action-height',
      `${Math.ceil(this.getBoundingClientRect().height)}px`
    );
  }

  handleViewportChange() {
    if (!this.media.matches) {
      this.hide();
      return;
    }

    this.initVisibilityObserver();
    this.syncPageSpace();
  }

  handleVisualViewport() {
    if (!window.visualViewport) return;

    const keyboardLikelyOpen = window.visualViewport.height < window.innerHeight * 0.72;
    document.body.classList.toggle('gvs-mobile-keyboard-open', keyboardLikelyOpen);
  }

  normaliseOptionValue(value) {
    const cleanValue = String(value || '').trim();
    if (!cleanValue || cleanValue.toLowerCase() === 'default title') return '';

    const lowerValue = cleanValue.toLowerCase();
    if (NEGATIVE_OPTION_VALUES.has(lowerValue)) return '';
    if (lowerValue === 'yes please' || lowerValue === 'yes') return 'Included';

    return cleanValue;
  }

  getSelectedOptions() {
    const scope = this.getScope();
    const options = [];

    scope.querySelectorAll('fieldset.variant-option').forEach((fieldset, index) => {
      const checked = fieldset.querySelector('input:checked');
      if (!checked) return;

      const value = this.normaliseOptionValue(checked.value);
      if (!value) return;

      const name = fieldset.dataset.optionName?.trim();
      const prefix = index === 0 || !name ? '' : `${name.replace(/^Add\s+/i, '')}: `;
      options.push(`${prefix}${value}`);
    });

    if (!options.length) {
      scope.querySelectorAll('select.variant-option__select').forEach((select, index) => {
        const value = this.normaliseOptionValue(select.value);
        if (!value) return;

        const wrapper = select.closest('.variant-option');
        const name = wrapper?.dataset.optionName?.trim();
        const prefix = index === 0 || !name ? '' : `${name.replace(/^Add\s+/i, '')}: `;
        options.push(`${prefix}${value}`);
      });
    }

    return options;
  }

  allOptionsSelected() {
    if (!this.hasVariants) return true;

    const scope = this.getScope();
    const fieldsets = Array.from(scope.querySelectorAll('fieldset.variant-option'));

    if (fieldsets.length) {
      return fieldsets.every((fieldset) => Boolean(fieldset.querySelector('input:checked')));
    }

    const selects = Array.from(scope.querySelectorAll('select.variant-option__select'));
    if (selects.length) return selects.every((select) => Boolean(select.value));

    return true;
  }

  updateVariantLabel(resource = null) {
    if (!this.variantLabel) return;

    const selectedOptions = this.getSelectedOptions();
    const resourceTitle = this.normaliseOptionValue(resource?.title);

    if (!selectedOptions.length && resourceTitle) selectedOptions.push(resourceTitle);

    this.variantLabel.textContent = selectedOptions.slice(0, 2).join(' · ');
  }

  formatMoney(cents) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';

    return new Intl.NumberFormat(this.locale, {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
  }

  updatePrice(resource = null) {
    if (!this.priceLabel || !resource || !Number.isFinite(Number(resource.price))) return;

    const taxable = resource.taxable !== false;
    const incVatCents = Number(resource.price);

    this.priceLabel.textContent = `${this.formatMoney(incVatCents)}${taxable ? ' inc VAT' : ''}`;
  }

  setButtonState(state, label) {
    this.button.dataset.state = state;
    this.button.classList.remove(
      'sticky-atc-bar__btn--atc',
      'sticky-atc-bar__btn--options',
      'sticky-atc-bar__btn--added',
      'sticky-atc-bar__btn--sold-out'
    );

    const className = {
      atc: 'sticky-atc-bar__btn--atc',
      loading: 'sticky-atc-bar__btn--atc',
      choose: 'sticky-atc-bar__btn--options',
      added: 'sticky-atc-bar__btn--added',
      soldout: 'sticky-atc-bar__btn--sold-out',
    }[state];

    if (className) this.button.classList.add(className);

    this.buttonText.textContent = label;
    this.button.disabled = state === 'soldout' || state === 'added' || state === 'loading';
    this.button.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
  }

  updateButtonState(resource = null) {
    this.updateVariantLabel(resource);
    this.updatePrice(resource);

    const realButton = this.getRealButton();
    const unavailable = resource ? resource.available === false : Boolean(realButton?.disabled);

    if (unavailable) {
      this.setButtonState('soldout', 'Sold out');
      return;
    }

    if (this.hasVariants && !this.allOptionsSelected()) {
      this.setButtonState('choose', 'Choose options');
      return;
    }

    this.setButtonState('atc', 'Add to basket');
  }

  handleVariantSelected() {
    this.setButtonState('loading', 'Updating…');
  }

  handleVariantUpdate(event) {
    const eventProductId = String(event.detail?.data?.productId || '');
    if (this.productId && eventProductId && eventProductId !== this.productId) return;

    this.updateButtonState(event.detail?.resource || null);
  }

  handleOptionChange(event) {
    if (!(event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)) return;
    if (!event.target.closest('.variant-picker')) return;

    window.requestAnimationFrame(() => this.updateVariantLabel());
  }

  handleClick() {
    const state = this.button.dataset.state;

    if (state === 'choose') {
      this.focusVariantPicker();
      return;
    }

    if (state !== 'atc') return;

    const realButton = this.getRealButton();
    if (!realButton || realButton.disabled) {
      this.updateButtonState();
      return;
    }

    this.pendingAdd = true;
    this.setButtonState('loading', 'Adding…');

    if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
    this.pendingTimer = window.setTimeout(() => {
      this.pendingTimer = null;
      if (!this.pendingAdd) return;

      this.pendingAdd = false;
      this.updateButtonState();
    }, 15000);

    realButton.click();
  }

  focusVariantPicker() {
    const picker = this.getVariantPicker();
    if (!picker) return;

    const top = picker.getBoundingClientRect().top + window.scrollY - this.getHeaderHeight() - 16;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });

    this.hide();

    window.setTimeout(() => {
      const firstControl = picker.querySelector('input:not(:checked), select, button');
      firstControl?.focus({ preventScroll: true });
    }, 350);
  }

  handleCartUpdate(event) {
    if (!this.pendingAdd) return;

    const eventProductId = String(event.detail?.data?.productId || '');
    if (this.productId && eventProductId && eventProductId !== this.productId) return;

    this.pendingAdd = false;
    if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
    this.pendingTimer = null;
    this.showAddedState();
  }

  handleCartError() {
    if (!this.pendingAdd) return;

    this.pendingAdd = false;
    if (this.pendingTimer) window.clearTimeout(this.pendingTimer);
    this.pendingTimer = null;
    this.updateButtonState();
  }

  showAddedState() {
    if (this.addedTimer) window.clearTimeout(this.addedTimer);

    this.setButtonState('added', 'Added ✓');

    this.addedTimer = window.setTimeout(() => {
      this.updateButtonState();
    }, 2200);
  }
}

if (!customElements.get('gvs-sticky-atc')) {
  customElements.define('gvs-sticky-atc', GVSStickyAtc);
}
